import React, { forwardRef } from "react";
import styled, { css, keyframes } from "styled-components";
import { tokens } from "../../public/tokens";
import { MOBILE_BREAKPOINT } from "./breakpoints";

const barEnter = keyframes({
    from: { opacity: 0, transform: "translate(-50%, 8px)" },
    to: { opacity: 1, transform: "translate(-50%, 0)" },
});

const barEnterInline = keyframes({
    from: { opacity: 0, transform: "translateY(-4px)" },
    to: { opacity: 1, transform: "translateY(0)" },
});

/*
 * Every $pinned-dependent property here is its own per-property function,
 * rather than one top-level props function returning the whole object, for
 * the same reason documented in Modal.js: `animation` must vary by $pinned
 * and wraps a keyframes() result in css``, which only flattens correctly
 * when the surrounding object is a plain top-level object, not one returned
 * from a props function.
 */
const StyledBar = styled.div({
    position: ({ $pinned = true }) => ($pinned ? "fixed" : "relative"),
    bottom: ({ $pinned = true }) => ($pinned ? tokens.globals["spacing-xl"] : "auto"),
    left: ({ $pinned = true }) => ($pinned ? "50%" : "auto"),
    transform: ({ $pinned = true }) => ($pinned ? "translateX(-50%)" : "none"),
    width: "max-content",
    maxWidth: `calc(100vw - ${tokens.globals["spacing-8"]})`,
    padding: tokens.globals["spacing-md"],
    background: tokens.colors.surfaces["container-background"],
    boxShadow: tokens.effects.effects.shadow.hover,
    borderRadius: tokens.globals["border-radius"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.globals["spacing-md"],
    flexWrap: "wrap",
    zIndex: 1000,
    animation: ({ $pinned = true }) =>
        css`${$pinned ? barEnter : barEnterInline} 200ms cubic-bezier(0.16, 1, 0.3, 1)`,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        width: ({ $pinned = true }) =>
            $pinned ? `calc(100vw - ${tokens.globals["spacing-8"]})` : "100%",
    },
});

const StyledSummary = styled.p({
    margin: 0,
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["base-16px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: tokens.typography.lineHeights["110"],
    whiteSpace: "nowrap",
});

const StyledActions = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-sm"],
    flexWrap: "wrap",
});

/**
 * Bottom-pinned (or inline) bar used for bulk-selection actions, e.g. the
 * Linked Programmes "N Programmes selected" action bar.
 * @param {React.ReactNode} props.summary Selection summary text (e.g. "3 Programmes selected").
 * @param {React.ReactNode} props.children Action controls rendered on the right.
 * @param {boolean} [props.pinned] Fixes the bar to the bottom-center viewport; false renders inline.
 * @param {string} [props.className] Optional class name.
 * @param {string} [props.ariaLabel] Accessible name for the bar's region.
 */
const FloatingActionBar = forwardRef(function FloatingActionBar(
    {
        summary,
        children,
        pinned = true,
        className,
        ariaLabel = "Bulk actions",
    },
    ref
) {
    return (
        <StyledBar
            ref={ref}
            $pinned={pinned}
            className={className}
            role="region"
            aria-label={ariaLabel}
        >
            <StyledSummary aria-live="polite">{summary}</StyledSummary>
            <StyledActions>{children}</StyledActions>
        </StyledBar>
    );
});

FloatingActionBar.displayName = "FloatingActionBar";

export default FloatingActionBar;
